/**
 * BattleRoomCharacterSheet - Unified Character Display for BattleRoom
 * 
 * @date_of_change: 2025-09-06
 * @purpose: Combine CharacterSheetViewer's sophisticated data access with tabbed interface
 * @rule_reference: AGENTS.md - same functions that work on Dashboard should be used for BattleRoom
 * 
 * This component uses the exact same data access logic as CharacterSheetViewer but
 * is optimized for BattleRoom's token-based props. It provides the same tabbed 
 * interface with Stats, Spells, Equipment, Biography, and Personality tabs.
 */

import React, { useState, useEffect, useMemo } from 'react';
import HPFormatBridge from '../../../utils/HPFormatBridge';
import UnifiedDataManager from '../../../services/UnifiedDataManager';
import { selectCombatantSheetData } from '../../../utils/battleRoom/BattleRoomCombatantSelectors';
import CharacterActionEconomyPanel from '../../CharacterActionEconomyPanel';
import { buildCharacterInventoryRows } from '../../../utils/CharacterSheetReferencePresentation';
import { buildCharacterWeaponTableEntries, isShieldLikeEntry } from '../../../utils/CharacterSheetWeaponPresentation';
import { isAmmunitionEntry } from '../../../utils/EquipmentState';

import '../../CharacterSheetViewer/CharacterSheetViewer.css';

const asArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const normalizeEquipmentTextKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/\s+/g, ' ');

const normalizeEquipmentEntry = (entry) => (
  typeof entry === 'string'
    ? { name: entry }
    : (entry && typeof entry === 'object' ? entry : null)
);

const getEquipmentTypeLabel = (entry) => String(
  entry?.type
  || entry?.kind
  || entry?.category
  || entry?.itemType
  || ''
).trim();

const getEquipmentQuantity = (entry) => (
  typeof entry?.quantity === 'number' && Number.isFinite(entry.quantity)
    ? entry.quantity
    : 1
);

const collectBattleRoomInventoryEntries = (character) => {
  const equipment = character?.equipment && typeof character.equipment === 'object' && !Array.isArray(character.equipment)
    ? character.equipment
    : {};
  const inventoryEquipment = character?.inventory?.equipment && typeof character.inventory.equipment === 'object' && !Array.isArray(character.inventory.equipment)
    ? character.inventory.equipment
    : {};

  return [
    ...asArray(equipment.armor),
    ...asArray(equipment.items),
    ...asArray(equipment.equipment),
    ...asArray(equipment.other),
    ...asArray(inventoryEquipment.armor),
    ...asArray(inventoryEquipment.items),
    ...asArray(inventoryEquipment.equipment),
    ...asArray(inventoryEquipment.other),
    ...asArray(equipment.weapons).filter((entry) => isShieldLikeEntry(entry) || isAmmunitionEntry(entry)),
    ...asArray(inventoryEquipment.weapons).filter((entry) => isShieldLikeEntry(entry) || isAmmunitionEntry(entry)),
    ...asArray(character?.weapons).filter((entry) => isShieldLikeEntry(entry) || isAmmunitionEntry(entry)),
    ...asArray(character?.items),
    ...(Array.isArray(character?.inventory?.equipment) ? character.inventory.equipment.filter(Boolean) : []),
    ...(Array.isArray(character?.equipment) ? character.equipment.filter(Boolean) : [])
  ];
};

const buildBattleRoomArmorRows = (character) => {
  const rows = [];
  const seen = new Set();

  collectBattleRoomInventoryEntries(character).forEach((entry) => {
    const normalizedEntry = normalizeEquipmentEntry(entry);
    if (!normalizedEntry) return;

    const name = String(normalizedEntry.name || normalizedEntry.title || '').trim();
    if (!name) return;

    const typeLabel = getEquipmentTypeLabel(normalizedEntry);
    const armorClass = normalizedEntry?.armorClass ?? normalizedEntry?.ac ?? null;
    const armorLike = Boolean(
      isShieldLikeEntry(normalizedEntry)
      || armorClass != null
      || /\barmor\b/i.test(typeLabel)
    );
    if (!armorLike) return;

    const quantity = getEquipmentQuantity(normalizedEntry);
    const rarity = typeof normalizedEntry?.rarity === 'string' ? normalizedEntry.rarity.trim() : '';
    const effect = typeof normalizedEntry?.effect === 'string'
      ? normalizedEntry.effect.trim()
      : '';

    const key = [
      normalizeEquipmentTextKey(name),
      armorClass ?? '',
      quantity,
      normalizeEquipmentTextKey(typeLabel),
      normalizeEquipmentTextKey(rarity),
      normalizeEquipmentTextKey(effect)
    ].join('|');
    if (seen.has(key)) return;
    seen.add(key);

    rows.push({
      key,
      name,
      armorClass,
      quantity,
      rarity,
      type: typeLabel,
      effect
    });
  });

  rows.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  return rows;
};

const formatSignedModifier = (value) => (
  value >= 0 ? `+${value}` : `${value}`
);

const formatWeaponStatLine = (weaponRow) => {
  const segments = [];

  if (weaponRow?.attackBonus != null) {
    segments.push(`${formatSignedModifier(weaponRow.attackBonus)} to hit`);
  }
  if (weaponRow?.damageText) {
    const damageText = /\bdamage\b/i.test(weaponRow.damageText)
      ? weaponRow.damageText
      : `${weaponRow.damageText} damage`;
    segments.push(damageText);
  }
  if (weaponRow?.rangeText) {
    segments.push(/^(range|reach)\b/i.test(weaponRow.rangeText)
      ? weaponRow.rangeText
      : `Range ${weaponRow.rangeText}`);
  }

  return segments.join(', ');
};

const formatArmorStatLine = (armorRow) => {
  const segments = [];

  if (armorRow?.armorClass != null) {
    segments.push(`AC: ${armorRow.armorClass}`);
  }
  if (armorRow?.quantity > 1) {
    segments.push(`x${armorRow.quantity}`);
  }
  if (armorRow?.rarity) {
    segments.push(armorRow.rarity);
  }
  if (armorRow?.type && !/^(armor|shield)$/i.test(armorRow.type)) {
    segments.push(armorRow.type);
  }

  return segments.join(', ');
};

const formatInventoryStatLine = (itemRow) => {
  const segments = [];

  if (itemRow?.quantity > 1) {
    segments.push(`x${itemRow.quantity}`);
  }
  if (itemRow?.type) {
    segments.push(itemRow.type);
  }
  if (itemRow?.rarity) {
    segments.push(itemRow.rarity);
  }
  if (itemRow?.weight) {
    segments.push(`Weight ${itemRow.weight}`);
  }

  return segments.join(', ');
};

// Helper function to get character template by ID using UnifiedDataManager (SSOT)
const getCharacterTemplate = async (templateId) => {
  if (!templateId) {
    return null;
  }
  
  try {
    const map = await UnifiedDataManager.getAllEntities('character');
    const list = Object.values(map || {});
    const lower = String(templateId).toLowerCase();
    const match = list.find(e => {
      const id = (e.id || '').toLowerCase();
      const d = e.data || {};
      const nm = (d.identity?.name || d.name || '').toLowerCase();
      return id === lower || nm === lower;
    });
    if (match) {
      console.log(`[BattleRoomCharacterSheet] Found template via UnifiedDataManager: ${match.data?.identity?.name || match.data?.name || match.id}`);
      return { id: match.id, name: match.data?.identity?.name || match.data?.name || match.id, data: match.data, _src: 'UDM' };
    }
    console.log('[BattleRoomCharacterSheet] No UDM match for templateId:', templateId);
    return null;
  } catch (error) {
    console.error(`[BattleRoomCharacterSheet] Error getting character template:`, error);
    return null;
  }
};

const BattleRoomCharacterSheet = ({ token, focusTab = null }) => {
  const [activeTab, setActiveTab] = useState('stats');
  const [templateData, setTemplateData] = useState(null);

  useEffect(() => {
    const allowedTabs = new Set(['stats', 'spells', 'actions', 'equipment', 'biography', 'personality']);
    if (allowedTabs.has(focusTab)) {
      // date_of_change: 2026-06-07
      // WHAT: Let the action panel open the character sheet to the relevant tab.
      // WHY: Spell slots and weapons remain sheet-owned resources while combat actions still
      // provide immediate context when the player chooses a spell or attack.
      setActiveTab(focusTab);
    }
  }, [focusTab]);

  // Fetch template data when token changes and we need template lookup
  useEffect(() => {
    // Only fetch if we have a token with an ID but no character data
    if (token?.id && (!token.characterData || Object.keys(token.characterData).length === 0)) {
      const fetchTemplateData = async () => {
        try {
          const templateLookupId = token.baseTemplate || token.entityRef?.entityId || token.id;
          console.log(`[BattleRoomCharacterSheet] Fetching template data for: ${templateLookupId}`);
          const template = await getCharacterTemplate(templateLookupId);
          console.log(`[BattleRoomCharacterSheet] Successfully fetched template:`, template?.name);
          setTemplateData(template);
        } catch (error) {
          console.error(`[BattleRoomCharacterSheet] Error fetching template data:`, error);
          setTemplateData(null);
        }
      };
      
      fetchTemplateData();
    }
  }, [token]);

  // @date_of_change 2025-09-08 - FIXED: Use single source of truth following AGENTS.md
  const characterData = useMemo(() => {
    console.log('[BattleRoomCharacterSheet] Processing token data:', {
      hasToken: !!token,
      tokenId: token?.id,
      tokenName: token?.name,
      hasCharacterData: !!token?.characterData,
      hasTemplateData: !!templateData
    });
    
    const sourceData = token ? {
      ...(templateData && typeof templateData === 'object' ? templateData : {}),
      ...token,
      data: token?.data || templateData?.data,
      schemaData: token?.schemaData || templateData?.schemaData || templateData?.data,
      canonicalData: token?.canonicalData || templateData?.canonicalData || templateData?.data,
      originalUnifiedEntity: token?.originalUnifiedEntity || templateData?.originalUnifiedEntity || null,
      entityPayload: token?.entityPayload || templateData?.entityPayload || templateData?.data,
      characterData: token?.characterData || templateData?.characterData || undefined
    } : templateData;
    
    if (sourceData) {
      try {
        const resolvedCharacterData = selectCombatantSheetData(sourceData);

        console.log('[BattleRoomCharacterSheet] Using canonical character data directly:', {
          name: resolvedCharacterData.name,
          hasSpells: !!resolvedCharacterData.spells,
          hasEquipment: !!resolvedCharacterData.equipment,
          hasBiography: !!resolvedCharacterData.biography,
          hasAbilities: !!resolvedCharacterData.abilities,
          spellKeys: resolvedCharacterData.spells ? Object.keys(resolvedCharacterData.spells) : 'none',
          equipmentKeys: resolvedCharacterData.equipment ? Object.keys(resolvedCharacterData.equipment) : 'none',
          abilityKeys: resolvedCharacterData.abilities ? Object.keys(resolvedCharacterData.abilities) : 'none',
          hp: resolvedCharacterData.hp,
          maxHp: resolvedCharacterData.maxHp,
          ac: resolvedCharacterData.ac,
          tokenHasAbilities: !!sourceData.abilities,
          tokenAbilityKeys: sourceData.abilities ? Object.keys(sourceData.abilities) : 'none',
          tokenHP: sourceData.hp,
          tokenMaxHP: sourceData.maxHp,
          tokenAC: sourceData.ac
        });

        return resolvedCharacterData;
      } catch (error) {
        console.error('[BattleRoomCharacterSheet] Error accessing character data:', error);
        // Return default character following canonical schema
        return {
          id: 'default-character',
          name: 'Unknown Character',
          level: 1,
          class: 'Fighter',
          race: 'Human',
          background: 'Folk Hero',
          abilities: {
            strength: 10, dexterity: 10, constitution: 10,
            intelligence: 10, wisdom: 10, charisma: 10
          },
          hp: 10,
          maxHp: 10,
          ac: 10,
          initiative: 0,
          speed: 30,
          proficiencyBonus: 2,
          biography: {},
          features: [],
          spells: {},
          equipment: {},
          skills: {},
          savingThrows: {}
        };
      }
    }
    
    console.log('[BattleRoomCharacterSheet] No source data available, using default character');
    return {
      id: 'default-character',
      name: 'Unknown Character',
      level: 1,
      class: 'Fighter',
      race: 'Human',
      background: 'Folk Hero',
      abilities: {
        strength: 10, dexterity: 10, constitution: 10,
        intelligence: 10, wisdom: 10, charisma: 10
      },
      hp: 10,
      maxHp: 10,
      ac: 10,
      initiative: 0,
      speed: 30,
      proficiencyBonus: 2,
      biography: {},
      features: [],
      spells: {},
      equipment: {},
      skills: {},
      savingThrows: {}
    };
  }, [token, templateData]);

  // Utility functions (same as CharacterSheetViewer)
  const getAbilityModifier = (score) => {
    return Math.floor((score - 10) / 2);
  };

  const formatModifier = (modifier) => {
    return modifier >= 0 ? `+${modifier}` : `${modifier}`;
  };

  // Standard D&D ability order
  const STANDARD_ABILITIES = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];

  // Render functions (same as CharacterSheetViewer)
  const renderAbilityScores = () => {
    // @date_of_change 2025-09-19 - Updated to match monster sheet compact format per user request
    console.log('[BattleRoomCharacterSheet] Rendering ability scores with data:', {
      hasAbilities: !!characterData.abilities,
      abilitiesKeys: characterData.abilities ? Object.keys(characterData.abilities) : [],
      dataSource: characterData.id || characterData.name,
      dataSourceType: characterData.isCustom ? 'custom' : 'template'
    });

    const orderedAbilities = STANDARD_ABILITIES.map(ability => {
      let score = 10;

      if (characterData.abilities && characterData.abilities[ability] !== undefined) {
        score = parseInt(characterData.abilities[ability]) || 10;
      } else if (characterData.abilitiesShort) {
        const shortName = ability.substring(0, 3);
        if (characterData.abilitiesShort[shortName] !== undefined) {
          score = parseInt(characterData.abilitiesShort[shortName]) || 10;
        }
      } else if (characterData.stats) {
        const shortName = ability.substring(0, 3);
        if (characterData.stats[shortName] !== undefined) {
          score = parseInt(characterData.stats[shortName]) || 10;
        }
      } else if (characterData[ability] !== undefined) {
        score = parseInt(characterData[ability]) || 10;
      } else {
        const shortName = ability.substring(0, 3);
        if (characterData[shortName] !== undefined) {
          score = parseInt(characterData[shortName]) || 10;
        }
      }

      return { ability, score };
    });

    return (
      <div className="battle-sheet-abilities">
        {orderedAbilities.map(({ability, score}) => {
          const modifier = getAbilityModifier(score);
          const displayName = ability.substring(0, 3).toUpperCase();

          return (
            <div key={ability} className="battle-sheet-ability">
              <div className="battle-sheet-ability-name">{displayName}</div>
              <div className="battle-sheet-ability-score">{score}</div>
              <div className="battle-sheet-ability-modifier">{formatModifier(modifier)}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCombatStats = () => {
    const unifiedHP = HPFormatBridge.getHPFromEntity(characterData);
    const currentHP = unifiedHP?.current || 0;
    const maxHP = unifiedHP?.max || 1;
    const armorClass = characterData.ac || characterData.armorClass || 10;
    const speed = characterData.speed || 30;
    const profBonus = characterData.proficiencyBonus || Math.max(2, Math.floor((characterData.level || 1) / 4) + 2);
    
    const hpPercentage = maxHP > 0 ? (currentHP / maxHP) * 100 : 0;
    let hpColor = '#4caf50';
    
    if (hpPercentage <= 25) {
      hpColor = '#f44336';
    } else if (hpPercentage <= 50) {
      hpColor = '#ff9800';
    } else if (hpPercentage <= 75) {
      hpColor = '#ffeb3b';
    }
    
    return (
      <div className="monster-combat-stats">
        <div className="stat-row">
          <span className="stat-label">Armor Class</span>
          <span className="stat-value">{armorClass}</span>
        </div>
        <div className="stat-row">
          <span className="stat-label">Hit Points</span>
          <span className="stat-value" style={{ color: hpColor }}>
            {currentHP}/{maxHP}
          </span>
        </div>
        <div className="stat-row">
          <span className="stat-label">Speed</span>
          <span className="stat-value">{speed} ft.</span>
        </div>
        <div className="stat-row">
          <span className="stat-label">Proficiency</span>
          <span className="stat-value">+{profBonus}</span>
        </div>
      </div>
    );
  };

  const renderSpellSlots = () => {
    let spellcasting = null;
    let spellSlots = null;
    
    if (characterData.data?.spellcasting) {
      spellcasting = characterData.data.spellcasting;
      spellSlots = characterData.data.spellcasting.slots || characterData.data.spellcasting.spellSlots;
    } else if (characterData.spellcasting) {
      spellcasting = characterData.spellcasting;
      spellSlots = characterData.spellcasting.slots || characterData.spellcasting.spellSlots;
    } else if (characterData.canonicalData?.spells?.slots) {
      spellcasting = characterData.canonicalData.spells;
      spellSlots = characterData.canonicalData.spells.slots;
    } else if (characterData.spells?.slots) {
      spellcasting = characterData.spells;
      spellSlots = characterData.spells.slots;
      console.log('[BattleRoomCharacterSheet] Found spell slots in direct spells.slots path (custom character):', spellSlots);
    }
    
    if (!spellcasting || !spellSlots) {
      // date_of_change: 2026-06-07
      // WHAT: Stop generating half-caster spell slots in the sheet renderer.
      // WHY: Spell slots are combat resources owned by the canonical token snapshot; render-time
      // generation can reset used counts and make the sheet disagree with the engine after casts.
      return (
        <div className="spell-slots">
          <h4>Spell Slots</h4>
          <p>No spell slots available</p>
        </div>
      );
    }

    return (
      <div className="spell-slots-container">
        <div className="spell-slots-header"><span>Spell Slots</span></div>
        {Object.entries(spellSlots).map(([level, slots]) => {
          const max = typeof slots === 'object' ? (slots.total ?? slots.max ?? 0) : slots;
          const used = typeof slots === 'object' ? (slots.used ?? 0) : 0;
          
          return (
            <div key={level} className="spell-slot-row">
              <span className="slot-level">Level {level}:</span>
              <div className="spell-slots-grid">
                {Array.from({ length: max }, (_, i) => (
                  <div 
                    key={i} 
                    className={`spell-slot ${i < used ? 'active' : ''}`}
                  />
                ))}
              </div>
              <span className="slot-count">{max - used}/{max}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderPreparedSpells = () => {
    let spellcasting = null;
    let preparedSpellIds = [];
    
    console.log('[BattleRoomCharacterSheet] DEBUG - Full characterData structure for spells:', {
      hasData: !!characterData.data,
      hasSpellcasting: !!characterData.spellcasting,
      hasDataSpellcasting: !!characterData.data?.spellcasting,
      dataSpellcastingKeys: characterData.data?.spellcasting ? Object.keys(characterData.data.spellcasting) : 'none'
    });
    
    if (characterData.data?.spellcasting?.prepared) {
      spellcasting = characterData.data.spellcasting;
      preparedSpellIds = characterData.data.spellcasting.prepared;
      console.log('[BattleRoomCharacterSheet] Found prepared spells in EntityStorage path data.spellcasting.prepared:', preparedSpellIds);
    } else if (characterData.customData?.spellcasting?.prepared) {
      spellcasting = characterData.customData.spellcasting;
      preparedSpellIds = characterData.customData.spellcasting.prepared;
      console.log('[BattleRoomCharacterSheet] Found prepared spells in CustomAsset path customData.spellcasting.prepared:', preparedSpellIds);
    } else if (characterData.spellcasting?.prepared) {
      spellcasting = characterData.spellcasting;
      preparedSpellIds = characterData.spellcasting.prepared;
      console.log('[BattleRoomCharacterSheet] Found prepared spells in root path spellcasting.prepared:', preparedSpellIds);
    } else if (characterData.preparedSpells) {
      preparedSpellIds = characterData.preparedSpells;
      spellcasting = characterData.canonicalData?.spells || characterData.spells || {};
      console.log('[BattleRoomCharacterSheet] Found prepared spells in mapped path preparedSpells (custom character):', preparedSpellIds);
    } else if (characterData.canonicalData?.spells?.known) {
      preparedSpellIds = characterData.canonicalData.spells.known;
      spellcasting = characterData.canonicalData.spells;
      console.log('[BattleRoomCharacterSheet] Found prepared spells in custom character path canonicalData.spells.known:', preparedSpellIds);
    } else if (characterData.spells?.prepared) {
      // @date_of_change 2025-09-08 - ARCHITECTURAL FIX: Add canonical spells.prepared path for direct canonical data
      preparedSpellIds = characterData.spells.prepared;
      spellcasting = characterData.spells;
      console.log('[BattleRoomCharacterSheet] Found prepared spells in canonical spells.prepared path:', preparedSpellIds);
    } else if (characterData.spells?.known) {
      // @date_of_change 2025-09-06 - CRITICAL FIX: Add direct spells.known path for custom characters like Elandor
      preparedSpellIds = characterData.spells.known;
      spellcasting = characterData.spells;
      console.log('[BattleRoomCharacterSheet] Found prepared spells in direct spells.known path (custom character):', preparedSpellIds);
    } else if (characterData.data?.spellcasting) {
      spellcasting = characterData.data.spellcasting;
      preparedSpellIds = [];
      console.log('[BattleRoomCharacterSheet] Found data.spellcasting but no prepared array, keys:', Object.keys(characterData.data.spellcasting));
    } else if (characterData.spellcasting) {
      spellcasting = characterData.spellcasting;
      preparedSpellIds = [];
      console.log('[BattleRoomCharacterSheet] Found spellcasting but no prepared array, keys:', Object.keys(characterData.spellcasting));
    } else {
      console.log('[BattleRoomCharacterSheet] No spellcasting data found in any path');
    }
    
    // date_of_change: 2025-09-17 - Determine by-level spells container per ZERO TRANSFORMATION
    // Preferred: top-level characterData.spells (UDM). Fallback: data.spells or root spells with by-level keys
    const byLevelContainer = (characterData.spells && (characterData.spells.cantrips || characterData.spells.level1 || characterData.spells['1']))
      ? characterData.spells
      : (characterData.data?.spells && (characterData.data.spells.cantrips || characterData.data.spells.level1 || characterData.data.spells['1']))
        ? characterData.data.spells
        : null;
    console.log('[BattleRoomCharacterSheet] By-level spells container detection:', {
      hasTopLevelSpells: !!characterData.spells,
      hasDataSpells: !!characterData.data?.spells,
      usingByLevelContainer: !!byLevelContainer,
      byLevelKeys: byLevelContainer ? Object.keys(byLevelContainer) : []
    });

    if (!spellcasting) {
      console.log('[BattleRoomCharacterSheet] No spellcasting data found');
      return null;
    }

    // Create a component to handle the spell loading with hooks (same as CharacterSheetViewer)
    const PreparedSpellsContent = () => {
      const [spellDatabase, setSpellDatabase] = React.useState(null);
      
      React.useEffect(() => {
        fetch('/refdata/spells/index.json')
          .then(response => response.json())
          .then(spells => {
            const spellMap = {};
            spells.forEach(spell => {
              spellMap[spell.id] = spell;
              spellMap[spell.name] = spell;
              spellMap[spell.name.toLowerCase()] = spell;
            });
            setSpellDatabase(spellMap);
          })
          .catch(error => {
            console.error('[BattleRoomCharacterSheet] Failed to load spell database:', error);
          });
      }, []);

      if (!spellDatabase) {
        return <p>Loading spells...</p>;
      }

      const spellsByLevel = {};
      const missingSpells = [];
      
      // date_of_change: 2025-09-17 - PRIMARY PATH: Use by-level container (entity.spells) and intersect with prepared list
      if (byLevelContainer && (byLevelContainer.level1 || byLevelContainer.level2 || byLevelContainer.cantrips || byLevelContainer['1'])) {
        const keys = ['cantrips','level1','level2','level3','level4','level5','level6','level7','level8','level9'];
        keys.forEach((levelKey, index) => {
          const rawLevelSpells = byLevelContainer[levelKey] || [];
          if (rawLevelSpells.length === 0) return;
          const level = levelKey === 'cantrips' ? 0 : index;
          if (!spellsByLevel[level]) spellsByLevel[level] = [];

          rawLevelSpells.forEach(spellEntry => {
            const entryKey = typeof spellEntry === 'string' ? spellEntry : (spellEntry?.name || spellEntry?.id || '');
            if (!entryKey) return;

            // If a prepared list exists, only include those
            const isPrepared = preparedSpellIds && preparedSpellIds.length > 0
              ? preparedSpellIds.some(ps => {
                  if (typeof ps === 'string') return ps.toLowerCase() === entryKey.toLowerCase();
                  if (ps && typeof ps === 'object') {
                    const psKey = ps.name || ps.id;
                    return psKey && psKey.toLowerCase() === entryKey.toLowerCase();
                  }
                  return false;
                })
              : true; // No prepared list → show all
            if (!isPrepared) return;

            // Prefer full embedded spell object if present
            const embeddedDesc = typeof spellEntry === 'object' ? (spellEntry.description || spellEntry.text || spellEntry.desc) : null;
            const embeddedSchool = typeof spellEntry === 'object' ? (spellEntry.school || spellEntry.tradition) : null;
            const embeddedRange = typeof spellEntry === 'object' ? spellEntry.range : null;
            const embeddedCastingTime = typeof spellEntry === 'object' ? (spellEntry.castingTime || spellEntry.time) : null;
            const embeddedDuration = typeof spellEntry === 'object' ? spellEntry.duration : null;

            if (embeddedDesc) {
              spellsByLevel[level].push({
                id: spellEntry.id || entryKey,
                name: spellEntry.name || entryKey,
                level,
                school: embeddedSchool || '',
                castingTime: embeddedCastingTime || '',
                range: embeddedRange || '',
                duration: embeddedDuration || '',
                description: embeddedDesc
              });
              return;
            }

            // Fallback to spell database
            const lookupKey = entryKey;
            let spellData = spellDatabase[lookupKey] || spellDatabase[lookupKey.toLowerCase()] ||
                            spellDatabase[lookupKey.replace(/'/g, "'")] || spellDatabase[lookupKey.replace(/'/g, "'").toLowerCase()];
            if (spellData) {
              spellsByLevel[level].push({
                id: entryKey,
                name: spellData.name,
                level,
                school: spellData.school,
                castingTime: spellData.castingTime,
                range: spellData.range,
                duration: spellData.duration,
                description: spellData.description
              });
            } else {
              missingSpells.push(entryKey);
              spellsByLevel[level].push({ id: entryKey, name: entryKey, level });
            }
          });
        });
      } else {
        // Fallback path: No by-level container available → build from prepared list using database
        preparedSpellIds.forEach(spellEntry => {
          const key = typeof spellEntry === 'string' ? spellEntry : (spellEntry?.name || spellEntry?.id || '');
          if (!key) return;
          const keyLower = key.toLowerCase ? key.toLowerCase() : String(key).toLowerCase();
          let spellData = spellDatabase[key] || spellDatabase[keyLower] || spellDatabase[key.replace(/'/g, "'")] || spellDatabase[key.replace(/'/g, "'").toLowerCase()];

          const level = typeof spellData?.level === 'number' ? spellData.level : 1;
          if (!spellsByLevel[level]) spellsByLevel[level] = [];

          if (spellData) {
            spellsByLevel[level].push({
              id: key,
              name: spellData.name,
              level,
              school: spellData.school,
              castingTime: spellData.castingTime,
              range: spellData.range,
              duration: spellData.duration,
              description: spellData.description
            });
          } else {
            missingSpells.push(key);
            spellsByLevel[level].push({ id: key, name: key, level });
          }
        });
      }

      // @date_of_change: 2025-09-08 - Reduced console noise by logging missing spells as single summary
      if (missingSpells.length > 0) {
        console.debug(`[BattleRoomCharacterSheet] ${missingSpells.length} spells not found in database:`, missingSpells.join(', '));
      }

      return (
        <>
          {Object.keys(spellsByLevel)
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map(level => (
              <details key={level} className="spell-level-section" open>
                <summary className="spell-level-header">
                  <h5>{level === '0' ? 'Cantrips' : `Level ${level}`} ({spellsByLevel[level].length})</h5>
                </summary>
                <div className="spell-list">
                  {spellsByLevel[level].map((spell, index) => (
                    <div key={`${spell.id}-${index}`} className="prepared-spell">
                      <div className="spell-name">{spell.name}</div>
                      {spell.school && (
                        <div className="spell-details">
                          <span className="spell-school">{spell.school}</span>
                          {spell.castingTime && <span className="spell-time"> • {spell.castingTime}</span>}
                          {spell.range && <span className="spell-range"> • {spell.range}</span>}
                        </div>
                      )}
                      {spell.description && (
                        <div className="spell-description">{spell.description}</div>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            ))}
          {Object.keys(spellsByLevel).length === 0 && (
            <p>No prepared spells</p>
          )}
        </>
      );
    };

    return (
      <div className="prepared-spells">
        <h4>Prepared Spells</h4>
        <PreparedSpellsContent />
      </div>
    );
  };

  const weaponRows = useMemo(() => buildCharacterWeaponTableEntries(characterData || {}), [characterData]);
  const armorRows = useMemo(() => buildBattleRoomArmorRows(characterData || {}), [characterData]);
  const itemRows = useMemo(() => {
    const armorKeys = new Set(armorRows.map((row) => normalizeEquipmentTextKey(row.name)));
    const inventoryEntries = collectBattleRoomInventoryEntries(characterData || {});

    return buildCharacterInventoryRows(characterData || {})
      .filter((row) => !armorKeys.has(normalizeEquipmentTextKey(row.name)))
      .map((row) => {
        const metadata = inventoryEntries.find((entry) => {
          const normalizedEntry = normalizeEquipmentEntry(entry);
          if (!normalizedEntry) return false;
          const entryName = String(normalizedEntry.name || normalizedEntry.title || '').trim();
          return normalizeEquipmentTextKey(entryName) === normalizeEquipmentTextKey(row.name);
        });

        return {
          ...row,
          type: metadata ? getEquipmentTypeLabel(metadata) : '',
          rarity: typeof metadata?.rarity === 'string' ? metadata.rarity.trim() : '',
          effect: typeof metadata?.effect === 'string' ? metadata.effect.trim() : ''
        };
      });
  }, [armorRows, characterData]);

  if (!characterData) {
    return (
      <div className="character-sheet-loading">
        <p>Loading character data...</p>
      </div>
    );
  }

  // Render with tabbed interface (same as CharacterSheetViewer)
  return (
    <div className="character-sheet-viewer">
      <div className="character-header">
        <h2>{characterData.name}</h2>
        <div className="character-info">
          <span className="character-info-pill">Level {characterData.level}</span>
          {characterData.race && (
            <span className="character-info-pill">{characterData.race}</span>
          )}
          {characterData.class && (
            <span className="character-info-pill">{characterData.class}</span>
          )}
          {characterData.experiencePoints !== undefined && characterData.experiencePoints > 0 && (
            <div className="character-xp">
              <span>XP: {characterData.experiencePoints.toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>

      <div className="sheet-tabs">
        <button 
          className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          Stats
        </button>
        <button 
          className={`tab ${activeTab === 'spells' ? 'active' : ''}`}
          onClick={() => setActiveTab('spells')}
        >
          Spells
        </button>
        <button 
          className={`tab ${activeTab === 'actions' ? 'active' : ''}`}
          onClick={() => setActiveTab('actions')}
        >
          Actions
        </button>
        <button 
          className={`tab ${activeTab === 'equipment' ? 'active' : ''}`}
          onClick={() => setActiveTab('equipment')}
        >
          Equipment
        </button>
        <button 
          className={`tab ${activeTab === 'biography' ? 'active' : ''}`}
          onClick={() => setActiveTab('biography')}
        >
          Biography
        </button>
        <button 
          className={`tab ${activeTab === 'personality' ? 'active' : ''}`}
          onClick={() => setActiveTab('personality')}
        >
          Personality
        </button>
      </div>

      <div className="sheet-content">
        {activeTab === 'stats' && (
          <div className="stats-tab">
            {renderAbilityScores()}
            {renderCombatStats()}
          </div>
        )}
        
        {activeTab === 'spells' && (characterData.data?.spellcasting || characterData.spellcasting || characterData.spells) && (
          <div className="spells-tab">
            <div className="spellcasting-info">
              {(() => {
                const spellcasting = characterData.data?.spellcasting
                  || characterData.spellcasting
                  || characterData.spells
                  || characterData.canonicalData?.spells;
                const abilities = characterData.data?.abilities
                  || characterData.abilities
                  || characterData.canonicalData?.abilities;
                const proficiencyBonusRaw = characterData.data?.combatStats?.proficiencyBonus
                  ?? characterData.combatStats?.proficiencyBonus
                  ?? characterData.proficiencyBonus;
                
                if (!spellcasting) return null;
                
                const spellAbility = spellcasting.ability;
                const canonicalAttackBonus = Number(
                  spellcasting.attackBonus
                  ?? spellcasting.spellAttackBonus
                  ?? spellcasting.toHit
                );
                const canonicalSaveDC = Number(
                  spellcasting.saveDC
                  ?? spellcasting.saveDc
                  ?? spellcasting.spellSaveDC
                );
                let spellAttackBonus = Number.isFinite(canonicalAttackBonus) ? canonicalAttackBonus : null;
                let spellSaveDC = Number.isFinite(canonicalSaveDC) ? canonicalSaveDC : null;

                if ((spellAttackBonus == null || spellSaveDC == null) && abilities && spellAbility) {
                  const abilityScore = Number(abilities[spellAbility]);
                  const proficiencyBonus = Number(proficiencyBonusRaw);
                  if (Number.isFinite(abilityScore) && Number.isFinite(proficiencyBonus)) {
                    const modifier = Math.floor((abilityScore - 10) / 2);
                    const computedAttackBonus = modifier + proficiencyBonus;
                    if (spellAttackBonus == null) spellAttackBonus = computedAttackBonus;
                    if (spellSaveDC == null) spellSaveDC = 8 + computedAttackBonus;
                  }
                }

                if (spellAttackBonus == null && spellSaveDC == null) return null;
                
                return (
                  <>
                    {spellSaveDC != null && (
                      <div className="spell-stat">
                        <span className="label">Spell Save DC:</span>
                        <span className="value">{spellSaveDC}</span>
                      </div>
                    )}
                    {spellAttackBonus != null && (
                      <div className="spell-stat">
                        <span className="label">Spell Attack:</span>
                        <span className="value">{formatModifier(spellAttackBonus)}</span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
            {renderSpellSlots()}
            {renderPreparedSpells()}
          </div>
        )}
        
        {activeTab === 'actions' && (
          <div className="actions-tab">
            <CharacterActionEconomyPanel
              subject={characterData}
              emptyMessage="No derived combat actions available."
              compact
            />
          </div>
        )}
        
        {activeTab === 'equipment' && (
          <div className="equipment-tab">
            <div className="equipment-section">
              <h4>Equipped Weapons</h4>
              <div className="equipment-list">
                {weaponRows.length > 0 ? (
                  weaponRows.map((weapon) => (
                    <div key={weapon.key} className="equipment-item weapon">
                      <div className="item-name">{weapon.name}</div>
                      {formatWeaponStatLine(weapon) && (
                        <div className="item-stats">{formatWeaponStatLine(weapon)}</div>
                      )}
                      {weapon.notes && <div className="item-effect">{weapon.notes}</div>}
                    </div>
                  ))
                ) : (
                  <div className="no-equipment">
                    <p>No equipped weapons.</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="equipment-section">
              <h4>Armor & Items</h4>
              <div className="equipment-list">
                {armorRows.length > 0 || itemRows.length > 0 ? (
                  <>
                    {armorRows.map((item) => (
                      <div key={item.key} className="equipment-item armor">
                        <div className="item-name">{item.name}</div>
                        {formatArmorStatLine(item) && (
                          <div className="item-stats">{formatArmorStatLine(item)}</div>
                        )}
                        {item.effect && <div className="item-effect">{item.effect}</div>}
                      </div>
                    ))}
                    {itemRows.map((item) => (
                      <div key={item.key} className="equipment-item item">
                        <div className="item-name">{item.name}</div>
                        {formatInventoryStatLine(item) && (
                          <div className="item-stats">{formatInventoryStatLine(item)}</div>
                        )}
                        {item.effect && <div className="item-effect">{item.effect}</div>}
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="no-equipment">
                    <p>No armor or items found.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'biography' && (
          <div className="biography-tab">
            <div className="biography-section">
              <h4>Appearance</h4>
              <div className="biography-content">
                {characterData.appearance ? (
                  typeof characterData.appearance === 'object' ? (
                    <div>
                      {characterData.appearance.description && <p>{characterData.appearance.description}</p>}
                      <ul>
                        {characterData.appearance.age && <li><strong>Age:</strong> {characterData.appearance.age}</li>}
                        {characterData.appearance.height && <li><strong>Height:</strong> {characterData.appearance.height}</li>}
                        {characterData.appearance.weight && <li><strong>Weight:</strong> {characterData.appearance.weight}</li>}
                        {characterData.appearance.eyes && <li><strong>Eyes:</strong> {characterData.appearance.eyes}</li>}
                        {characterData.appearance.hair && <li><strong>Hair:</strong> {characterData.appearance.hair}</li>}
                        {characterData.appearance.skin && <li><strong>Skin:</strong> {characterData.appearance.skin}</li>}
                      </ul>
                    </div>
                  ) : (
                    <p>{characterData.appearance}</p>
                  )
                ) : (
                  <p className="no-content">No appearance description available.</p>
                )}
              </div>
            </div>
            
            <div className="biography-section">
              <h4>Background</h4>
              <div className="biography-content">
                {characterData.background || characterData.data?.background ? (
                  <p><strong>{characterData.background || characterData.data?.background}</strong></p>
                ) : (
                  <p className="no-content">No background specified.</p>
                )}
              </div>
            </div>
            
            <div className="biography-section">
              <h4>Backstory</h4>
              <div className="biography-content">
                {characterData.biography?.backstory || characterData.backstory || characterData.history || characterData.data?.backstory ? (
                  <p>{characterData.biography?.backstory || characterData.backstory || characterData.history || characterData.data?.backstory}</p>
                ) : (
                  <p className="no-content">No backstory available.</p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'personality' && (
          <div className="personality-tab">
            <div className="personality-section">
              <h4>Personality Traits</h4>
              <div className="personality-content">
                {characterData.personalityTraits || 
                 characterData.personalTraits || 
                 characterData.data?.personalTraits?.personality ||
                 characterData.personalTraits?.personality ||
                 characterData.canonicalData?.personalTraits?.personality ? (
                  <p>{characterData.personalityTraits || 
                      characterData.personalTraits || 
                      characterData.data?.personalTraits?.personality ||
                      characterData.personalTraits?.personality ||
                      characterData.canonicalData?.personalTraits?.personality}</p>
                ) : (
                  <p className="no-content">No personality traits listed.</p>
                )}
              </div>
            </div>
            
            <div className="personality-section">
              <h4>Ideals</h4>
              <div className="personality-content">
                {characterData.ideals || 
                 characterData.data?.personalTraits?.ideals ||
                 characterData.personalTraits?.ideals ||
                 characterData.canonicalData?.personalTraits?.ideals ? (
                  <p>{characterData.ideals || 
                      characterData.data?.personalTraits?.ideals ||
                      characterData.personalTraits?.ideals ||
                      characterData.canonicalData?.personalTraits?.ideals}</p>
                ) : (
                  <p className="no-content">No ideals listed.</p>
                )}
              </div>
            </div>
            
            <div className="personality-section">
              <h4>Bonds</h4>
              <div className="personality-content">
                {characterData.bonds || 
                 characterData.data?.personalTraits?.bonds ||
                 characterData.personalTraits?.bonds ||
                 characterData.canonicalData?.personalTraits?.bonds ? (
                  <p>{characterData.bonds || 
                      characterData.data?.personalTraits?.bonds ||
                      characterData.personalTraits?.bonds ||
                      characterData.canonicalData?.personalTraits?.bonds}</p>
                ) : (
                  <p className="no-content">No bonds listed.</p>
                )}
              </div>
            </div>
            
            <div className="personality-section">
              <h4>Flaws</h4>
              <div className="personality-content">
                {characterData.flaws || 
                 characterData.data?.personalTraits?.flaws ||
                 characterData.personalTraits?.flaws ||
                 characterData.canonicalData?.personalTraits?.flaws ? (
                  <p>{characterData.flaws || 
                      characterData.data?.personalTraits?.flaws ||
                      characterData.personalTraits?.flaws ||
                      characterData.canonicalData?.personalTraits?.flaws}</p>
                ) : (
                  <p className="no-content">No flaws listed.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BattleRoomCharacterSheet;
